import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAppStatusActions } from '@/db/useAppStatusActions';
import { appStatusLiveState, maintenanceEditDocState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { DEFAULT_MAINTENANCE_MESSAGE } from '@rock/util';
import type { AppStatusMaintenance, Timestamp } from '@rock/types';
import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import maintenanceShot from '@/assets/app-status/maintenance.png';
import { AppStatusGuidanceCard, LiveSince } from './AppStatusGuidanceCard';
import { AppStatusStagingToolbar } from './AppStatusStagingToolbar';

const FormSchema = z.object({
    enabled: z.boolean(),
    message: z.string(),
});

type FormValues = z.infer<typeof FormSchema>;

function toFormValues(doc: AppStatusMaintenance | undefined): FormValues {
    return {
        enabled: !!doc?.enabled,
        message: doc?.message ?? '',
    };
}

function toDoc(values: FormValues): AppStatusMaintenance {
    return {
        object: 'app-status-maintenance',
        schema: 1,
        id: 'maintenance',
        enabled: values.enabled,
        message: values.message.trim() || undefined,
        updatedAt: new Date().toISOString() as Timestamp,
    };
}

export const MaintenancePage: FC = () => {
    const editDoc = useAsyncAtomValue(maintenanceEditDocState, undefined);
    const live = useAsyncAtomValue(appStatusLiveState('maintenance'), undefined);
    const { busy, saveDraft } = useAppStatusActions('maintenance');

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: toFormValues(undefined),
    });

    useEffect(() => {
        if (editDoc && !form.formState.isDirty) {
            form.reset(toFormValues(editDoc));
        }
    }, [editDoc, form]);

    const onSubmit = async (values: FormValues) => {
        await saveDraft(toDoc(values));
        form.reset(values);
    };

    return (
        <div className="p-6 max-w-3xl space-y-4">
            <AppStatusStagingToolbar
                kind="maintenance"
                confirmMessage={
                    editDoc?.enabled
                        ? 'This is the kill switch: EVERY user gets a full-screen "temporarily unavailable" screen ' +
                          'and cannot use the app until you disable and re-publish this.'
                        : undefined
                }
            />
            <AppStatusGuidanceCard
                title="Maintenance Mode (kill switch)"
                screenshot={{ src: maintenanceShot, alt: 'Full-screen maintenance overlay' }}
            >
                <p>
                    Shows a full-screen, non-dismissable "We'll be right back" screen to every user — app and web,
                    signed-in or not. Use it for serious incidents where continuing to use the app would make things
                    worse (e.g. a data bug you're repairing).
                </p>
                <p>
                    It applies live within seconds of publishing, and lifts the same way. Devices in preview mode get an
                    on-screen escape pill, so previewing a staged draft can't lock you out of your own device.
                </p>
            </AppStatusGuidanceCard>

            <Card>
                <CardHeader>
                    <CardTitle>Kill Switch</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="enabled"
                                render={({ field }) => (
                                    <FormItem className="flex items-center gap-3">
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                        <FormLabel>Maintenance mode</FormLabel>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="message"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Message</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                className="bg-white"
                                                placeholder={DEFAULT_MAINTENANCE_MESSAGE}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Shown under "We'll be right back". Leave blank for the default above.
                                        </FormDescription>
                                    </FormItem>
                                )}
                            />
                            <div className="flex items-center gap-4">
                                <Button type="submit" disabled={form.formState.isSubmitting || busy}>
                                    {form.formState.isSubmitting ? 'Saving…' : 'Save Draft'}
                                </Button>
                                <LiveSince live={live} />
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>

            <div className="section-description">
                Saving stages a draft — preview it on your device via Settings → "Preview staged content" (the blocked
                screen shows a "tap to exit preview" pill), then Publish above to make it live.
            </div>
        </div>
    );
};
